import React, { useRef, useState } from 'react';
import { Plus, Trash2, Upload, FileText, X, KeySquare, Link2, Loader2 } from 'lucide-react';
import { upload } from '@vercel/blob/client';

export const emptyVehiclesData = {
  vins: [''],
  fileName: null,
  fileUrl: null,
  googleDocUrl: '',
};

// Valid when the user has supplied vehicle info via at least one method:
// a typed VIN, an uploaded file, or a Google Doc link.
export const validateVehicles = (v) => {
  const hasVin = Array.isArray(v.vins) && v.vins.some((vin) => vin && vin.trim());
  const hasFile = !!v.fileUrl;
  const hasDoc = !!(v.googleDocUrl && v.googleDocUrl.trim());
  if (!hasVin && !hasFile && !hasDoc) {
    return { vehicles: 'Add at least one vehicle — enter a VIN, upload a document, or share a Google Doc link.' };
  }
  return {};
};

const VehiclesStep = ({ value, onChange, errors = {} }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const updateVin = (index, vin) => {
    const vins = [...value.vins];
    vins[index] = vin.toUpperCase();
    onChange({ ...value, vins });
  };

  const addVin = () => onChange({ ...value, vins: [...value.vins, ''] });

  const removeVin = (index) => {
    const vins = value.vins.filter((_, i) => i !== index);
    onChange({ ...value, vins: vins.length ? vins : [''] });
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      // Direct browser → Vercel Blob upload; the token is minted by our
      // serverless /api/blob-upload endpoint. Files land in the
      // "pre-onboarding/" folder (a path prefix on the blob name).
      const blob = await upload(`pre-onboarding/${file.name}`, file, {
        access: 'public',
        handleUploadUrl: '/api/blob-upload',
      });
      onChange({ ...value, fileName: file.name, fileUrl: blob.url });
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    onChange({ ...value, fileName: null, fileUrl: null });
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div>
      <p className="preob-step__eyebrow">Your Vehicles</p>
      <p className="preob-step__lead">
        Add your vehicle VINs using any method below. You can use one or combine all three &mdash; whatever
        is easiest for you.
      </p>

      {errors.vehicles && <p className="preob-form__error preob-step__error">{errors.vehicles}</p>}

      {/* Method 1: VIN fields */}
      <section className="preob-vmethod">
        <div className="preob-vmethod__head">
          <KeySquare size={16} />
          <h3 className="preob-vmethod__title">Enter VINs manually</h3>
        </div>
        <div className="preob-vins">
          {value.vins.map((vin, i) => (
            <div key={i} className="preob-vin-row">
              <input
                value={vin}
                onChange={(e) => updateVin(i, e.target.value)}
                placeholder={`17-digit VIN #${i + 1}`}
                maxLength={17}
                className="preob-input preob-input--mono"
                aria-label={`Vehicle VIN number ${i + 1}`}
              />
              <span className="preob-vin-count">{vin.length}/17</span>
              <button
                type="button"
                onClick={() => removeVin(i)}
                className="preob-icon-btn"
                aria-label={`Remove VIN ${i + 1}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addVin} className="preob-link-btn">
          <Plus size={16} />
          Add another vehicle
        </button>
      </section>

      <div className="preob-or">
        <span className="preob-or__line" />
        <span className="preob-or__text">OR</span>
        <span className="preob-or__line" />
      </div>

      {/* Method 2: Upload */}
      <section className="preob-vmethod">
        <div className="preob-vmethod__head">
          <Upload size={16} />
          <h3 className="preob-vmethod__title">Upload a document with your VINs</h3>
        </div>

        {value.fileName ? (
          <div className="preob-file">
            <div className="preob-file__info">
              <FileText size={16} />
              <span className="preob-file__name">{value.fileName}</span>
            </div>
            <button
              type="button"
              onClick={clearFile}
              className="preob-icon-btn preob-icon-btn--sm"
              aria-label="Remove uploaded file"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading}
            className="preob-dropzone"
          >
            {uploading ? (
              <>
                <Loader2 size={20} className="preob-spin" />
                <span className="preob-dropzone__title">Uploading…</span>
              </>
            ) : (
              <>
                <Upload size={20} />
                <span className="preob-dropzone__title">Click to upload</span>
                <span className="preob-dropzone__hint">CSV, XLSX, PDF, or DOC up to 10MB</span>
              </>
            )}
          </button>
        )}
        {uploadError && <p className="preob-file__error">{uploadError}</p>}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.txt"
          className="preob-hidden-input"
          onChange={(e) => handleFile(e.target.files && e.target.files[0])}
        />
      </section>

      <div className="preob-or">
        <span className="preob-or__line" />
        <span className="preob-or__text">OR</span>
        <span className="preob-or__line" />
      </div>

      {/* Method 3: Google Doc link */}
      <section className="preob-vmethod">
        <div className="preob-vmethod__head">
          <Link2 size={16} />
          <h3 className="preob-vmethod__title">Share a Google Doc link</h3>
        </div>
        <input
          type="url"
          inputMode="url"
          value={value.googleDocUrl}
          onChange={(e) => onChange({ ...value, googleDocUrl: e.target.value })}
          placeholder="https://docs.google.com/document/..."
          className="preob-input"
          aria-label="Google Doc link with VINs"
        />
        <p className="preob-vmethod__note">
          Make sure to give Rentid view access to the document so we can read your VINs.
        </p>
      </section>
    </div>
  );
};

export default VehiclesStep;
